/* 
## Odometer ##
Animate number values like an odometer
*/
import gsap from 'gsap';

const ANIMATION_TIME = .8;
const ANIMATION_DELAY = .1;
const ANIMATION_SLOW_DOWN_F = 1.5;
const ANIMATION_STAGGER = .25;

export const odometer = (el, start = true) => {
  if (el.classList.contains('r-odometer--init')) return;
  initOdometer(el);
  if (start) {
    odometerStart(el);
  }
}

/**
 * ### INIT ###
 */
const initOdometer = (el) => {
  // store oryginal value
  el.dataset.value = el.innerHTML.trim();
  // split value letter by letter
  el.innerHTML = "";
  const items = [];
  let minWidth = 0;
  el.dataset.value.split('').map( (char, i) => {
    // wrap each char in span
    const wrapper = document.createElement('span');
    wrapper.classList.add('r-odometer__item');
    const spacer = document.createElement('span');
    spacer.classList.add('r-odometer__item__spacer');
    spacer.innerHTML = char;
    const digit = document.createElement('span');
    digit.classList.add('r-odometer__item__digit');
    digit.innerHTML = char;
    const next = document.createElement('span');
    next.classList.add('r-odometer__item__next');
    wrapper.appendChild(spacer);
    wrapper.appendChild(digit);
    wrapper.appendChild(next);
    items.push(wrapper);
    el.appendChild(wrapper);
    // store min width
    const width = wrapper.offsetWidth;
    if (width > minWidth) minWidth = width;
    // check if is numeric
    if (isNaN(char)) {
      wrapper.classList.add('r-odometer__item--letter');
      initRotateLetter(wrapper, char);
    } else {
      wrapper.classList.add('r-odometer__item--number');
      initRotateNumber(wrapper, char);
    }
  });
  // set min width
  items.map( item => item.style.width = minWidth + 'px' );
  el.classList.add('r-odometer--init');
}
/**
 * ### START animation (after INIT) ###
 */
export const odometerStart = (el) => {
  if (el.classList.contains('r-odometer--started')) return;
  [...el.querySelectorAll('.r-odometer__item')].forEach((item, i) => {
    item.dataset.delay = i * ANIMATION_STAGGER;
    if (item.classList.contains('r-odometer__item--letter')) {
      animateRotateLetter(item);
    } else {
      animateRotateNumber(item);
    }
  });
  el.classList.add('r-odometer--started');
}

/**
 * ### NUMBER init & start animation ###
 */
// prepare to animate number
const initRotateNumber = (wrapper, number) => {
  const digit = wrapper.querySelector('.r-odometer__item__digit');
  digit.innerHTML = '';
  // prepare digits to animate
  let current = parseInt(number);
  let digits = [];
  while (current >= 0) {
    digits.push(current);
    current--;
  }
  digits.reverse();
  wrapper.dataset.digits = digits.join(',');
  const next = wrapper.querySelector('.r-odometer__item__next');
  gsap.set(next, { y: '100%' });
  next.innerHTML = digits[0];
}

// exponential animation times
const getAnimationTimes = (numElements, totalDuration, slowDownFactor) => {
  let times = [];
  let totalExponentialSum = 0;
  for (let i = 0; i < numElements; i++) {
    totalExponentialSum += Math.pow(slowDownFactor, i);
  }
  let baseTime = totalDuration / totalExponentialSum;
  for (let i = 0; i < numElements; i++) {
    times.push(baseTime * Math.pow(slowDownFactor, i));
  }
  return times;
}

const animateRotateNumber = (wrapper) => {
  // get digits string (data-digits)
  const digitsStr = wrapper.dataset.digits;
  // digits array as INTs
  const digits = digitsStr.split(',').map( digit => parseInt(digit));
  // digite DOM element!
  const digit = wrapper.querySelector('.r-odometer__item__digit');
  // next digit DOM element
  const next = wrapper.querySelector('.r-odometer__item__next');
  // current value of digit
  const current = digit.innerHTML ? parseInt(digit.innerHTML) : null;
  // if current value is last digit, return
  if (current === digits[digits.length - 1]) return;
  // next value
  let nextValue = null;
  if (digits.indexOf(current) < digits.length - 1) {
    nextValue = digits[digits.indexOf(current) + 1];
  } else {
    nextValue = digits[0];
  }
  // is last digit?
  const isLast = nextValue === digits[digits.length - 1];
  // duration of animation (exponential slow down)
  const durations = getAnimationTimes(digits.length, ANIMATION_TIME + parseFloat(wrapper.dataset.delay), ANIMATION_SLOW_DOWN_F);
  const duration = durations[digits.indexOf(nextValue)];

  const easingLast = "power.out";
  const ease = isLast ? easingLast : "none";
  if (current !== null) { 
    // set positions & content
    digit.innerHTML = current;
    gsap.set(digit, { y: '0%' });
    gsap.set(next, { y: '100%' });
    next.innerHTML = nextValue;
    gsap.to(digit, {
      y: '-100%',
      duration: duration - .01,
      ease: "none",
      onComplete: () => {
        digit.innerHTML = nextValue;
      }
    });
    gsap.to(next, {
      y: '0%',
      duration,
      ease,
      onComplete: animateRotateNumber, 
      onCompleteParams: [wrapper], 
    });

  } else {
    // first run
    digit.innerHTML = nextValue;
    gsap.set(digit, { y: '100%' });
    gsap.to(digit, { 
      y: '0%', 
      duration,
      ease,
      delay: ANIMATION_DELAY, 
      onComplete: animateRotateNumber, 
      onCompleteParams: [wrapper], 
    });
  }
}



/**
 * ### LETTER init & start animation ###
 */
// prepare to animate letter
const initRotateLetter = (wrapper, letter) => {
  const digit = wrapper.querySelector('.r-odometer__item__digit');
  digit.innerHTML = '';
  const next = wrapper.querySelector('.r-odometer__item__next');
  next.innerHTML = letter;
  gsap.set(next, { y: '100%' });
}
const animateRotateLetter = (wrapper) => {
  const next = wrapper.querySelector('.r-odometer__item__next');
  gsap.to(next, { 
    y: '0%', 
    duration: ANIMATION_TIME + parseFloat(wrapper.dataset.delay), 
    delay: ANIMATION_DELAY 
  });
}